import { useRef } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Eye, QrCode, Copy } from 'lucide-react';
import { toast } from 'sonner';
import { STATUS_STYLES, maskAccount } from '../../utils/bankConstants';
import { HoverCopyCell } from './HoverCopyCell';

const ROW_HEIGHT = 60;
const HEADER_HEIGHT = 44;

/**
 * Virtualized bank list — renders only the rows visible in the viewport.
 * Handles 100K+ banks with steady FPS and near-constant memory. Kicks in
 * automatically once the caller passes >200 rows.
 *
 * Columns match the existing `<table>` layout in BanksPage.jsx so the visual
 * experience is unchanged.
 */
export const VirtualizedBankTable = ({ banks, onView, height = 600 }) => {
  const parentRef = useRef(null);
  const rowVirtualizer = useVirtualizer({
    count: banks.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => ROW_HEIGHT,
    overscan: 8,
  });

  const cols = 'grid-cols-[minmax(180px,2fr)_minmax(120px,1fr)_minmax(140px,1.2fr)_minmax(120px,1fr)_minmax(100px,0.8fr)_minmax(90px,0.8fr)_minmax(140px,1fr)_100px]';

  return (
    <div className="w-full" data-testid="virtual-banks-table">
      <div className={`grid ${cols} bg-slate-50 border-b sticky top-0 z-10`} style={{ height: HEADER_HEIGHT }}>
        {['Company Name', 'Vendor', 'Account', 'UPI', 'Account Type', 'QR', 'Status', 'Actions'].map((h, i) => (
          <div key={h} className={`p-3 text-xs font-semibold text-slate-600 ${i === 7 ? 'text-center' : ''}`}>{h}</div>
        ))}
      </div>
      <div ref={parentRef} className="overflow-auto" style={{ height }}>
        <div style={{ height: `${rowVirtualizer.getTotalSize()}px`, position: 'relative', width: '100%' }}>
          {rowVirtualizer.getVirtualItems().map((vi) => {
            const bank = banks[vi.index];
            return (
              <div
                key={bank.id}
                data-testid={`all-bank-row-${bank.id}`}
                className={`grid ${cols} border-b hover:bg-slate-50 items-center absolute top-0 left-0 w-full`}
                style={{ height: `${vi.size}px`, transform: `translateY(${vi.start}px)` }}
              >
                <div className="p-3 truncate">
                  <div className="font-medium text-sm truncate">{bank.companyName || bank.bankName}</div>
                  {bank.companyName && <div className="text-xs text-slate-500 truncate">{bank.bankName}</div>}
                </div>
                <div className="p-3"><Badge variant="outline" className="text-xs font-medium">{bank.vendorName}</Badge></div>
                <div className="p-3"><HoverCopyCell value={bank.accountNumber} displayValue={maskAccount(bank.accountNumber)} label="Account" /></div>
                <div className="p-3"><HoverCopyCell value={bank.upi} displayValue={bank.upi ? '***' + bank.upi.slice(-6) : '-'} label="UPI" /></div>
                <div className="p-3 text-sm text-slate-600 truncate">{bank.accountType || 'Savings'}</div>
                <div className="p-3">
                  {bank.qrAvailable === 'yes' ? (
                    <div className="inline-flex items-center gap-1">
                      <QrCode className="w-4 h-4 text-green-600" />
                      <span className="text-xs text-green-700 font-medium">Yes</span>
                      {bank.qrDataUrl && (
                        <button
                          onClick={(e) => { e.stopPropagation(); navigator.clipboard.writeText(bank.qrDataUrl); toast.success('QR copied!'); }}
                          className="inline-flex items-center gap-1 text-xs bg-slate-800 text-white px-2 py-0.5 rounded ml-1 hover:bg-slate-900"
                        >
                          <Copy className="w-3 h-3" /> Copy
                        </button>
                      )}
                    </div>
                  ) : (
                    <span className="text-xs text-slate-400">No</span>
                  )}
                </div>
                <div className="p-3"><Badge className={STATUS_STYLES[bank.status] || 'bg-slate-100 text-slate-800'}>{bank.status}</Badge></div>
                <div className="p-3 text-center">
                  <Button onClick={() => onView(bank)} variant="outline" size="sm" className="gap-1" data-testid={`view-all-bank-${bank.id}`}>
                    <Eye className="w-3 h-3" /> View
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default VirtualizedBankTable;
